import type { AttendanceState } from './dutyEvent.constant';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  THREE DISPLAY STATES OVER SIX DERIVED ONES — `Not arrived · Present · Absent`. [C5c §5a]
 *
 *  `ATTENDANCE_STATES` is PA_REQUIREMENTS Appendix B's machine and it is untouched: the ladder
 *  (`attendanceOf`) still has one expression, and the wire still carries all six members. What
 *  the client asked for is what an officer and a supervisor READ, and that is three words:
 *  Present on any marking, Absent once the duty has ended with none, Not arrived while it runs.
 *
 *  ⚠️ THIS FILE HOLDS THE REDUCTION. IT HOLDS NO WORDS — `officerDutyState.constant.ts`'s rule,
 *  verbatim. `web/src/lib/labels.ts` states that display labels live on the client, and Duty
 *  Mitra sends every string through `t()`; English here would be untranslatable by construction.
 *  Both clients key their catalogue on `Presence`, and `statusWords.test.ts` pins the words.
 *
 *  ⚠️ IT IS `constant.ts` AND NOT `schema/`, FOR `dutyEvent.constant.ts`'s REASON: `pwa/` reads it
 *  at RUNTIME and every module in `schema/` imports zod, which its budget gate refuses.
 *
 *  ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ THE DAY BOUNDARY IS `ends_at`, NEVER MIDNIGHT — AND NOT ARRIVED BEGINS AT `starts_at`.
 *
 *  "Absent once the day has ended" reads as midnight and must not be built that way. A
 *  20:00→02:00 duty dated D is still running at 01:00 on D+1; a midnight rule would flip its
 *  officer to Absent while they can still mark, and back again when they do. `attendanceOf`
 *  already derives absence from `ends_at <= now` (FR-5.4.6, and the marking window closes at
 *  the same instant), and for a whole-day duty `generateSlots` stamps `ends_at` at 00:00 IST of
 *  the next day — so the rule already IS midnight exactly where midnight means anything.
 *  `new Date().toISOString().slice(0, 10)` would be a UTC midnight on top, which [IST-FIX] bans.
 *
 *  Before `starts_at` silence is not "not arrived" — nobody was due — so it counts as nothing,
 *  and §6d's attendance line is then absent rather than a row of zeros on an order issued three
 *  days early. The caller decides "started" (`starts_at <= now`), because only it holds the duty.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const PRESENCE_STATES = ['NOT_ARRIVED', 'PRESENT', 'ABSENT'] as const;
export type Presence = (typeof PRESENCE_STATES)[number];

/**
 * ⚠️ TOTAL OVER `AttendanceState`, AND `null` IS AN ANSWER — "this person is not counted here".
 *
 * `STARTED`, `REACHED` and `STOOD_DOWN` are all Present, because the brief's rule is "any
 * marking" — an officer who tapped Start Duty and never Mark Reached turned up. The dashboard's
 * tile folds `STARTED` into "not reached" and is left alone, deliberately: it answers a narrower
 * question ("how many made it to the point?") under a word that says so.
 *
 * ⚠️ `ABSENT` IS ABSENT EVEN BEFORE THE DUTY STARTS — an explicit commander marking is a stated
 * fact whatever the clock says. The SUMMARY lines count nothing before start anyway
 * (`presenceOfRows`), which is what keeps §6d true; this function answers for one person.
 *
 * ⚠️ `EXEMPT` IS NEVER COUNTED. FR-5.4.7: a supervisor's non-presence is expected and must never
 * surface as a red metric, and "not arrived" beside an Incharge's name would be exactly that.
 */
export const presenceOf = (state: AttendanceState, dutyStarted: boolean): Presence | null => {
  switch (state) {
    /*
     * ⚠️ THE COMMENT SITS ABOVE THE GROUPED LABELS, NEVER BETWEEN THEM — `no-fallthrough` reads a
     * comment between two case labels as a case with a body and no `break`. Any marking.
     */
    case 'STARTED':
    case 'REACHED':
    case 'STOOD_DOWN':
      return 'PRESENT';
    case 'ABSENT':
      return 'ABSENT';
    case 'NOT_STARTED':
      return dutyStarted ? 'NOT_ARRIVED' : null;
    case 'EXEMPT':
      return null;
  }
};

/** Three figures, one per display state. The wire's shape is `presenceTallySchema`. */
export type PresenceTally = { present: number; absent: number; notArrived: number };

/**
 * Which field of a tally each display state counts in — a `Record` over the tuple, so a fourth
 * state is a compile error here rather than a figure nobody adds up.
 */
export const PRESENCE_TALLY_KEY: Record<Presence, keyof PresenceTally> = {
  NOT_ARRIVED: 'notArrived',
  PRESENT: 'present',
  ABSENT: 'absent',
};

/**
 * ⚠️ ONE FOLD FOR EVERY SUMMARY LINE — the preview's, the order view's and the printed order's.
 *
 * TOTAL OVER THE DISPLAY STATES, so the three figures always sum to how many people were counted:
 * nobody is dropped between a state and a field. `null` entries are the people `presenceOf` says
 * are not counted, and they are skipped rather than folded into a figure.
 *
 * ⚠️ `null` WHEN NOTHING WAS COUNTED, AND A ZERO IS A CLAIM. `arrangementSchema`'s old `reached`
 * made the same distinction: `0 present · 0 absent · 0 not arrived` on an order that has not
 * started is a row of zeros that reads as a fault (§6d), where `null` says there is nothing to say.
 */
export const presenceTallyOf = (presences: Iterable<Presence | null>): PresenceTally | null => {
  const tally: PresenceTally = { present: 0, absent: 0, notArrived: 0 };
  let counted = 0;
  for (const presence of presences) {
    if (presence === null) continue;
    tally[PRESENCE_TALLY_KEY[presence]] += 1;
    counted += 1;
  }
  return counted === 0 ? null : tally;
};
